import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import type { IndividualEntityColumn } from '@/modules/investing/config/entity-columns'

// Status + Name are always visible (they're the sticky/pinned columns); hidden
// from the column-toggle dropdown so users can't break the table layout by
// turning them off. Matches the PINNED_KEYS convention in entities-page.vue.
const PINNED_KEYS = new Set(['lifecycle_state', 'name'])

interface UseEntityColumnVisibilityReturn {
  visibleColumnKeys: Ref<string[]>
  columns: ComputedRef<IndividualEntityColumn[]>
  columnOptions: ComputedRef<{ value: string; label: string }[]>
  gridTemplateColumns: ComputedRef<string>
}

// Pairs with `useIndividualEntityColumns` to wire up the column-visibility
// dropdown and dynamic grid template. Pulls all the boilerplate that used to
// live in the SPV/Funds list pages into one shared composable.
export function useEntityColumnVisibility(
  baseColumns: ComputedRef<IndividualEntityColumn[]>,
): UseEntityColumnVisibilityReturn {
  const defaultVisibleKeys = computed(() => baseColumns.value.filter((c) => !c.hidden).map((c) => c.key))

  const visibleColumnKeys = ref<string[]>([...defaultVisibleKeys.value])

  // When `baseColumns` changes (e.g., a conditional column becomes available
  // because cashAccessible flipped on), add the new defaults to the user
  // selection so they show up instead of staying invisibly toggled off.
  watch(defaultVisibleKeys, (keys) => {
    const existing = new Set(visibleColumnKeys.value)
    for (const k of keys) if (!existing.has(k)) visibleColumnKeys.value.push(k)
  })

  const columns = computed(() =>
    baseColumns.value.map((col) => ({
      ...col,
      // Pinned columns stay visible regardless of toggle state.
      hidden: PINNED_KEYS.has(col.key) ? false : !visibleColumnKeys.value.includes(col.key),
    })),
  )

  const columnOptions = computed(() =>
    baseColumns.value
      .filter((col) => !PINNED_KEYS.has(col.key))
      .map((col) => ({ value: col.key, label: col.label }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  )

  // Dynamic grid template: fixed widths for the pinned columns (caret 0, Status
  // 9rem, Name 18rem) + `minmax(max-content, 1fr)` per remaining visible
  // column. Fills the table width instead of leaving empty tracks on the right.
  const restColumnCount = computed(
    () => baseColumns.value.filter((c) => !PINNED_KEYS.has(c.key) && visibleColumnKeys.value.includes(c.key)).length,
  )
  const gridTemplateColumns = computed(() => {
    const rest = 'minmax(max-content, 1fr) '.repeat(restColumnCount.value).trim()
    return `0 9rem 18rem ${rest}`.trim()
  })

  return { visibleColumnKeys, columns, columnOptions, gridTemplateColumns }
}
